/**
 * ScenarioComparison - Premium Scenario Cards
 *
 * Premium fintech aesthetic - clean, minimal, professional.
 * Displays return timing scenarios side by side.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ScenarioComparison as ScenarioComparisonType } from '../../../api/_schemas/journeyOutput.schema';

interface ScenarioComparisonProps {
  comparison: ScenarioComparisonType;
  className?: string;
}

// Risk level styles - matching Projections theme
const RISK_STYLES = {
  low: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Low Risk' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Medium Risk' },
  high: { bg: 'bg-red-50', text: 'text-red-700', label: 'Higher Risk' },
} as const;

// Format corpus amount
function formatCorpus(amountINR: number, formatted?: string): string {
  if (amountINR === 0) return '₹0';
  const absAmount = Math.abs(amountINR);
  let formattedAbs: string;
  if (absAmount >= 10000000) formattedAbs = `₹${(absAmount / 10000000).toFixed(1)}Cr`;
  else if (absAmount >= 100000) formattedAbs = `₹${(absAmount / 100000).toFixed(1)}L`;
  else formattedAbs = `₹${absAmount.toLocaleString('en-IN')}`;
  if (amountINR < 0) return `${formattedAbs} less`;
  return formatted || formattedAbs;
}

// Scenario card styles - emerald for recommended
const CARD_STYLES = {
  recommended: {
    border: 'border-emerald-500',
    headerBg: 'bg-emerald-600',
    headerText: 'text-white',
    badge: 'Recommended',
  },
  tradeoff: {
    border: 'border-slate-300',
    headerBg: 'bg-slate-100',
    headerText: 'text-slate-700',
    badge: 'Trade-off',
  },
  risky: {
    border: 'border-amber-300',
    headerBg: 'bg-amber-100',
    headerText: 'text-amber-800',
    badge: 'Aggressive',
  },
  neutral: {
    border: 'border-slate-200',
    headerBg: 'bg-slate-50',
    headerText: 'text-slate-600',
    badge: 'Option',
  },
} as const;

// Get card style based on scenario
function getCardStyle(scenario: {
  isRecommended: boolean;
  metrics: { riskLevel?: 'low' | 'medium' | 'high' }
}) {
  if (scenario.isRecommended) return CARD_STYLES.recommended;
  const riskLevel = scenario.metrics?.riskLevel || 'medium';
  if (riskLevel === 'low') return CARD_STYLES.tradeoff;
  if (riskLevel === 'high') return CARD_STYLES.risky;
  return CARD_STYLES.neutral;
}

// Individual scenario card
interface ScenarioCardProps {
  scenario: ScenarioComparisonType['scenarios'][0];
  isSelected: boolean;
  onSelect: () => void;
  index: number;
  showCorpusAsHero?: boolean;
}

function ScenarioCard({ scenario, isSelected, onSelect, index, showCorpusAsHero = false }: ScenarioCardProps) {
  const metrics = scenario.metrics ?? {
    rnorYears: 0,
    taxSavingsINR: 0,
    taxSavingsFormatted: '₹0',
    additionalCorpusINR: 0,
    additionalCorpusFormatted: '₹0',
    riskLevel: 'medium' as const,
    riskExplanation: '',
  };

  const riskLevel = metrics.riskLevel ?? 'medium';
  const risk = RISK_STYLES[riskLevel] ?? RISK_STYLES.medium;
  const cardStyle = getCardStyle({ isRecommended: scenario.isRecommended, metrics: { riskLevel } });

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={onSelect}
      className={cn(
        'relative rounded-lg border-2 overflow-hidden cursor-pointer transition-all bg-white',
        cardStyle.border,
        isSelected && 'ring-2 ring-emerald-500 ring-offset-2'
      )}
    >
      {/* Header */}
      <div className={cn('px-4 py-2 flex items-center justify-between', cardStyle.headerBg)}>
        <span className={cn('text-sm font-medium', cardStyle.headerText)}>
          {cardStyle.badge}
        </span>
        {scenario.isCurrentPlan && (
          <span className="text-xs px-2 py-0.5 rounded bg-white/20 text-white">
            Your Plan
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Header */}
        <div className="text-center mb-4">
          <h4 className="text-base font-semibold text-slate-800">{scenario.name}</h4>
          <p className="text-sm text-slate-500">{scenario.returnDate}</p>
          <p className="text-xs text-slate-400">{scenario.returnFY}</p>
        </div>

        {/* Key Metrics */}
        <div className="space-y-3 mb-4">
          {/* Hero Metric */}
          {showCorpusAsHero ? (
            <div className="text-center p-3 bg-slate-50 rounded-md">
              <div className={cn(
                'text-xl font-semibold',
                (metrics.additionalCorpusINR ?? 0) < 0 ? 'text-red-600' :
                (metrics.additionalCorpusINR ?? 0) > 0 ? 'text-slate-800' : 'text-slate-600'
              )}>
                {(metrics.additionalCorpusINR ?? 0) > 0 ? '+' : ''}{formatCorpus(metrics.additionalCorpusINR ?? 0)}
              </div>
              <div className="text-xs text-slate-500">Extra Corpus vs Current</div>
            </div>
          ) : (
            <div className="text-center p-3 bg-slate-50 rounded-md">
              <div className="text-xl font-semibold text-slate-800">
                {metrics.taxSavingsFormatted || '₹0'}
              </div>
              <div className="text-xs text-slate-500">Tax Savings</div>
            </div>
          )}

          {/* Secondary metrics */}
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 bg-slate-50 rounded-md">
              <div className="text-lg font-semibold text-slate-800">{metrics.rnorYears ?? 0}</div>
              <div className="text-xs text-slate-500">RNOR Years</div>
            </div>
            <div className="text-center p-2 bg-slate-50 rounded-md">
              {showCorpusAsHero ? (
                <>
                  <div className="text-lg font-semibold text-slate-800">{metrics.taxSavingsFormatted || '₹0'}</div>
                  <div className="text-xs text-slate-500">Tax Savings</div>
                </>
              ) : (
                <>
                  <div className={cn(
                    'text-lg font-semibold',
                    (metrics.additionalCorpusINR ?? 0) < 0 ? 'text-red-600' : 'text-slate-800'
                  )}>
                    {formatCorpus(metrics.additionalCorpusINR ?? 0)}
                  </div>
                  <div className="text-xs text-slate-500">Extra Corpus</div>
                </>
              )}
            </div>
          </div>

          {/* Risk Level */}
          <div className={cn(
            'flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium',
            risk.bg, risk.text
          )}>
            {risk.label}
          </div>
        </div>

        {/* Pros */}
        {scenario.pros.length > 0 && (
          <div className="mb-3">
            <h5 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
              Advantages
            </h5>
            <ul className="space-y-1">
              {scenario.pros.map((pro, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-slate-400 flex-shrink-0">+</span>
                  <span>{pro}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cons */}
        {scenario.cons.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
              Trade-offs
            </h5>
            <ul className="space-y-1">
              {scenario.cons.map((con, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-amber-500 flex-shrink-0">-</span>
                  <span>{con}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Risk explanation when selected */}
        {isSelected && metrics.riskExplanation && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 pt-4 border-t border-slate-100"
          >
            <p className="text-xs text-slate-500">
              <span className="font-medium">Note:</span> {metrics.riskExplanation}
            </p>
          </motion.div>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-10 right-3">
          <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function ScenarioComparison({ comparison, className }: ScenarioComparisonProps) {
  if (!comparison || !comparison.scenarios || comparison.scenarios.length === 0) {
    return null;
  }

  // Detect if tax savings are similar across scenarios
  const taxSavingsValues = comparison.scenarios.map(s => s.metrics?.taxSavingsINR ?? 0);
  const maxTaxSavings = Math.max(...taxSavingsValues);
  const minTaxSavings = Math.min(...taxSavingsValues);
  const taxSavingsAreSimilar = maxTaxSavings > 0
    ? (maxTaxSavings - minTaxSavings) / maxTaxSavings < 0.1
    : true;

  const corpusValues = comparison.scenarios.map(s => s.metrics?.additionalCorpusINR ?? 0);
  const maxCorpus = Math.max(...corpusValues);
  const minCorpus = Math.min(...corpusValues);
  const corpusDiffers = maxCorpus !== minCorpus;

  // Hide section if no meaningful comparison exists
  // (tax savings similar AND corpus doesn't differ significantly)
  const noMeaningfulDifference = taxSavingsAreSimilar && !corpusDiffers;
  if (noMeaningfulDifference) {
    return null; // Don't show comparison when all scenarios are essentially the same
  }

  const showCorpusAsHero = taxSavingsAreSimilar && corpusDiffers;

  const [selectedId, setSelectedId] = useState<string>(comparison.recommendedScenarioId || comparison.scenarios[0]?.id || 'default');

  return (
    <div className={cn('space-y-5', className)}>
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Compare Return Scenarios</h3>
          <p className="text-sm text-slate-500">
            {showCorpusAsHero
              ? 'Tax savings are similar - compare corpus growth'
              : 'See how timing affects your outcomes'}
          </p>
        </div>
      </div>

      {/* Scenario Cards */}
      <div className={cn(
        'grid gap-4',
        comparison.scenarios.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
        comparison.scenarios.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      )}>
        {comparison.scenarios.map((scenario, index) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            isSelected={selectedId === scenario.id}
            onSelect={() => setSelectedId(scenario.id)}
            index={index}
            showCorpusAsHero={showCorpusAsHero}
          />
        ))}
      </div>

      {/* Recommendation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="rounded-lg bg-emerald-50 border border-emerald-200 p-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-600 flex items-center justify-center text-white flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-emerald-800 mb-1">Our Recommendation</h4>
            <p className="text-sm text-emerald-700 mb-3">{comparison.recommendationRationale}</p>
            <div className="p-3 bg-white rounded-md border border-emerald-200">
              <h5 className="text-xs font-medium text-emerald-600 uppercase tracking-wider mb-1">
                Key Trade-off
              </h5>
              <p className="text-sm text-slate-700">{comparison.keyTradeoff}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <p className="text-xs text-slate-400 text-center">
        Based on current tax laws and your profile. Consult a CA before making decisions.
      </p>
    </div>
  );
}

export default ScenarioComparison;
